import type { AisleZone, RackNode, RowConfig } from '../store/editorStore.ts'

const EPSILON = 0.001

export type AisleSide = 'above' | 'below' | 'left' | 'right'
export type DerivedAisleStatus = 'cold' | 'hot' | 'indeterminate'

export interface DerivedAisleResult {
  rowIds: [string, string]
  status: DerivedAisleStatus
  aisleType?: 'cold' | 'hot'
  side?: AisleSide
  x: number
  y: number
  w: number
  h: number
  widthM?: number
  reason?: string
}

type Bounds = { minX: number; minY: number; maxX: number; maxY: number }
export type AisleRack = Pick<RackNode, 'id' | 'x' | 'y' | 'width' | 'height'> & {
  orientation?: number
  rotation?: number
}

function normaliseAngle(value: number) {
  return ((value % 360) + 360) % 360
}

function rowAxis(row: RowConfig): 'horizontal' | 'vertical' {
  if (row.axis) return row.axis
  return [90, 270].includes(normaliseAngle(row.orientation)) ? 'vertical' : 'horizontal'
}

function rowFacing(row: RowConfig) {
  return normaliseAngle(row.facing ?? row.orientation)
}

function boundsOf(racks: AisleRack[]): Bounds | null {
  if (!racks.length) return null
  const bounds = racks.reduce<Bounds>((result, rack) => {
    const rotated = [90, 270].includes(normaliseAngle(rack.orientation ?? rack.rotation ?? 0))
    const extentX = rotated ? rack.height : rack.width
    const extentY = rotated ? rack.width : rack.height
    return {
      minX: Math.min(result.minX, rack.x),
      minY: Math.min(result.minY, rack.y),
      maxX: Math.max(result.maxX, rack.x + extentX),
      maxY: Math.max(result.maxY, rack.y + extentY),
    }
  }, { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity })
  return Object.values(bounds).every(Number.isFinite) ? bounds : null
}

function invalid(rowIds: [string, string], reason: string, boundsA?: Bounds | null, boundsB?: Bounds | null): DerivedAisleResult {
  const x = boundsA && boundsB ? Math.min(boundsA.minX, boundsB.minX) : 0
  const y = boundsA && boundsB ? Math.min(boundsA.minY, boundsB.minY) : 0
  const w = boundsA && boundsB ? Math.max(boundsA.maxX, boundsB.maxX) - x : 0
  const h = boundsA && boundsB ? Math.max(boundsA.maxY, boundsB.maxY) - y : 0
  return { rowIds, status: 'indeterminate', x, y, w, h, reason }
}

/** Derives an aisle only when two parallel rows have a clear physical gap. */
export function deriveAisle(
  first: RowConfig,
  second: RowConfig,
  firstRacks: AisleRack[],
  secondRacks: AisleRack[],
): DerivedAisleResult {
  const rowIds: [string, string] = [first.id, second.id]
  const firstBounds = boundsOf(firstRacks)
  const secondBounds = boundsOf(secondRacks)
  if (!firstBounds || !secondBounds) return invalid(rowIds, 'Для вычисления коридора нужны стойки в обоих рядах.', firstBounds, secondBounds)

  const firstAxis = rowAxis(first)
  if (firstAxis !== rowAxis(second)) return invalid(rowIds, 'Оси рядов различаются; стороны коридора не определяются.', firstBounds, secondBounds)

  let side: AisleSide
  let x: number
  let y: number
  let w: number
  let h: number
  let gap: number

  if (firstAxis === 'horizontal') {
    const overlapStart = Math.max(firstBounds.minX, secondBounds.minX)
    const overlapEnd = Math.min(firstBounds.maxX, secondBounds.maxX)
    if (overlapEnd - overlapStart <= EPSILON) return invalid(rowIds, 'Ряды не имеют продольного перекрытия для коридора.', firstBounds, secondBounds)
    if (firstBounds.maxY < secondBounds.minY) {
      side = 'below'; gap = secondBounds.minY - firstBounds.maxY
      x = overlapStart; y = firstBounds.maxY; w = overlapEnd - overlapStart; h = gap
    } else if (secondBounds.maxY < firstBounds.minY) {
      side = 'above'; gap = firstBounds.minY - secondBounds.maxY
      x = overlapStart; y = secondBounds.maxY; w = overlapEnd - overlapStart; h = gap
    } else return invalid(rowIds, 'Ряды пересекаются или соприкасаются по глубине.', firstBounds, secondBounds)
  } else {
    const overlapStart = Math.max(firstBounds.minY, secondBounds.minY)
    const overlapEnd = Math.min(firstBounds.maxY, secondBounds.maxY)
    if (overlapEnd - overlapStart <= EPSILON) return invalid(rowIds, 'Ряды не имеют продольного перекрытия для коридора.', firstBounds, secondBounds)
    if (firstBounds.maxX < secondBounds.minX) {
      side = 'right'; gap = secondBounds.minX - firstBounds.maxX
      x = firstBounds.maxX; y = overlapStart; w = gap; h = overlapEnd - overlapStart
    } else if (secondBounds.maxX < firstBounds.minX) {
      side = 'left'; gap = firstBounds.minX - secondBounds.maxX
      x = secondBounds.maxX; y = overlapStart; w = gap; h = overlapEnd - overlapStart
    } else return invalid(rowIds, 'Ряды пересекаются или соприкасаются по глубине.', firstBounds, secondBounds)
  }

  const firstFacing = rowFacing(first)
  const secondFacing = rowFacing(second)
  const frontToFront = side === 'below'
    ? firstFacing === 0 && secondFacing === 180
    : side === 'above'
      ? firstFacing === 180 && secondFacing === 0
      : side === 'right'
        ? firstFacing === 270 && secondFacing === 90
        : firstFacing === 90 && secondFacing === 270
  const backToBack = side === 'below'
    ? firstFacing === 180 && secondFacing === 0
    : side === 'above'
      ? firstFacing === 0 && secondFacing === 180
      : side === 'right'
        ? firstFacing === 90 && secondFacing === 270
        : firstFacing === 270 && secondFacing === 90

  if (!frontToFront && !backToBack) {
    return { rowIds, status: 'indeterminate', side, x, y, w, h, widthM: gap / 100, reason: 'Facing рядов не образует front-to-front или back-to-back.' }
  }

  const aisleType = frontToFront ? 'cold' : 'hot'
  return { rowIds, status: aisleType, aisleType, side, x, y, w, h, widthM: gap / 100 }
}

export function derivedAisleZone(result: DerivedAisleResult): AisleZone {
  return {
    id: `derived-aisle-${result.rowIds.join('--')}`,
    x: Math.round(result.x),
    y: Math.round(result.y),
    w: Math.round(result.w),
    h: Math.round(result.h),
    aisleType: result.aisleType ?? 'indeterminate',
    label: result.aisleType === 'cold' ? 'Холодный коридор' : result.aisleType === 'hot' ? 'Горячий коридор' : 'Ошибка ориентации коридора',
    derivedFromRowIds: result.rowIds,
    derivedStatus: result.status,
    derivedReason: result.reason,
    widthM: result.widthM,
    isDirty: true,
  }
}
